import { useCallback, useEffect, useRef, type MutableRefObject } from "react";
import type { Socket } from "socket.io-client";
import { readNotificationPreferences } from "../../../lib/notifications";
import { useDirectCall } from "../../calls/useDirectCall";

/**
 * One-to-one call wiring: ringtone muting rules, the direct-call hook itself, the refs the
 * service worker message handler uses to answer or dismiss a call, and auto-answering a call
 * accepted from the Android lock screen.
 */
export function useDirectCallWiring({
  socket,
  token,
  userId,
  livekitUrl,
  mutedSpaces,
  hiddenSpaces,
  answerCallRef,
  dismissCallRef,
}: {
  socket: Socket;
  token: string;
  userId?: string | null;
  livekitUrl: string;
  mutedSpaces: Record<string, boolean>;
  hiddenSpaces: Record<string, boolean>;
  answerCallRef: MutableRefObject<(() => void) | null>;
  dismissCallRef: MutableRefObject<(() => void) | null>;
}) {
  // Suppress the incoming-call ringtone when globally muted or this DM is
  // muted/hidden (the overlay still shows so the call can be answered silently).
  const isCallMuted = useCallback((spaceId: string | null) => {
    try { if (readNotificationPreferences().globalMute) return true; } catch { /* noop */ }
    const sid = spaceId ? String(spaceId) : '';
    if (!sid) return false;
    return !!(mutedSpaces[sid] || hiddenSpaces[sid]);
  }, [mutedSpaces, hiddenSpaces]);
  const directCall = useDirectCall({
    socket,
    token,
    selfUserId: userId ? String(userId) : null,
    livekitUrl,
    isCallMuted,
  });
  // Keep refs in sync so the SW message handler (mounted earlier) can drive calls
  answerCallRef.current = directCall.answerCall;
  dismissCallRef.current = directCall.dismissEnded;

  // Native Android lock-screen "Accept" opens the app with ?autoAnswerCallId=<id>
  // instead of answering itself: answering here means the call is accepted with
  // this web session's clientId — the one the LiveKit audio token is gated on —
  // so the user never has to tap Accept a second time. Captured once on mount and
  // stripped from the URL so a reload/back can't re-trigger an answer.
  const pendingAutoAnswerRef = useRef<string | null>(null);
  useEffect(() => {
    try {
      const id = new URLSearchParams(window.location.search).get('autoAnswerCallId');
      if (id) {
        pendingAutoAnswerRef.current = id;
        const url = new URL(window.location.href);
        url.searchParams.delete('autoAnswerCallId');
        window.history.replaceState({}, '', url.pathname + url.search);
      }
    } catch { /* noop */ }
  }, []);
  useEffect(() => {
    const pending = pendingAutoAnswerRef.current;
    if (!pending) return;
    const answerCall = directCall.answerCall;
    if (directCall.phase === 'incoming' && directCall.call?.id === pending && !directCall.busy) {
      pendingAutoAnswerRef.current = null;
      answerCall();
    }
  }, [directCall.phase, directCall.call?.id, directCall.busy, directCall.answerCall]);
  return directCall;
}
